<template>
    <div class="news-list">
        <div :class="{ shadow: scrolled }" class="header">
            <div class="search">
                <Search class="input"
                        placeholder="Поиск по продуктам"
                        @search="onInputQuery" />
            </div>
            <div v-if="!mobile" class="filter">
                <div class="row">
                    <div class="item-list">
                        <InlineSelect v-model="sortAscent"
                                      :options="dicts.sortAscent"
                                      :toTitle="it => it.name"
                                      class="item"
                                      label="Сортировка" />

                        <InlineMultiSelect v-model="model.eventTypes"
                                           :options="eventTypes"
                                           :toTitle="it => it.name"
                                           class="item"
                                           label="Тип новости" />
                    </div>

                    <button :disabled="emptyFilters" class="no-border action" @click="onClear">
                        <IconFilterClear class="icon" />
                    </button>
                </div>
                <div class="row">
                    <Toggle v-model="model.onlyUnread">Только непрочитанные</Toggle>
                </div>
            </div>
            <div v-else class="filter">
                <button class="iconed no-border action"
                        @click="filterSheet = 'HALF_VISIBLE'">
                    <IconSettings />
                </button>
            </div>
        </div>

        <div ref="middle" class="middle scroll">
            <InfinityScroll ref="scroll" :load-next="loadNextPage" root=".news-list .middle">
                <div v-if="notFound && this.nextPage !== 0" class="not-found">
                    <template v-if="model.onlyUnread">
                        <div class="title">Непрочитанных новостей нет</div>
                    </template>

                    <template v-else>
                        <div class="title">Ничего не найдено</div>
                        <div class="desc">
                            Нет ни одного совпадения по запросу.<br>
                            Измените критерии поиска.
                        </div>
                    </template>
                </div>
                <div v-else-if="notFound && emptyFiltersAndQuery" class="no-elements">
                    <div class="icon"></div>
                    <div class="text">Новостей пока нет</div>
                </div>

                <template v-else>
                    <CardNews v-for="it in elements"
                              :key="it.id"
                              ref="cardNews"
                              :mapEventTypes="mapEventTypes"
                              :subscriptionConfig="subscriptionConfig"
                              :value="it"
                              @closeModal="close"
                              @read="onRead"
                              @subscribed="$emit('updateSubscriptions')"
                              @updateConfig="updateSubscriptionConfig" />
                </template>

                <template #loader>
                    <CardNewsSkeleton v-for="it in 5" v-show="!notFound" :key="it" />
                </template>
            </InfinityScroll>
        </div>

        <div v-show="!notFound" class="footer">
            <button :disabled="unreadNewsCount === 0" class="action no-border iconed" @click="readAll">
                <span class="text">Сделать прочитанными</span>
                <IconEyeLarge class="icon" />
            </button>
        </div>

        <BottomSheet v-model="filterSheet" class="filter-sheet">
            <template #header>
                <div class="title">Фильтры</div>
                <button :disabled="emptyFilters"
                        class="no-border iconed"
                        @click="onClear">
                    <IconFilterClear />
                </button>
            </template>
            <template>
                <MobileInlineSelect v-model="sortAscent"
                                    :options="dicts.sortAscent"
                                    :toTitle="it => it.name"
                                    class="item"
                                    label="Сортировка" />

                <MobileInlineMultiSelect v-model="model.eventTypes"
                                         :options="eventTypes"
                                         :toTitle="it => it.name"
                                         class="item"
                                         label="Тип новости" />

                <Toggle v-model="model.onlyUnread" class="item">Только непрочитанные</Toggle>
            </template>
        </BottomSheet>
    </div>
</template>

<script>
import Local from '@/common/util/local.js';
import CardNewsSkeleton from '@/common/skeletons/CardNewsSkeleton.vue';
import { SUBSCRIPTION_CONFIG_KEY } from '@/common/page_comp/News/SubTab/common/config.js';
import Search from '@/common/comp/search/Search.vue';
import { toMap } from '@dsee/util/arrays.js';
import { debounceAsync } from '@dsee/util/async.js';
import { read, readAll, search } from '@/restapi/news/news.js';
import CardNews from './CardNews.vue';
import IconSettings from './icons/settings.svg?component';

const EVENT_TYPES_BASE = Object.freeze([
    { name: 'Новый документ', id: 'DOCUMENT_ADDED' },
    // { name: 'Новый проект', id: 'PILOT_ADDED' },
    // { name: 'Изменение статуса', id: 'STATUS_CHANGED' },
    { name: 'Новый продукт', id: 'CREATED' },
    // { name: 'Хороший старт', id: 'HIGH_RATED' },
    // { name: 'Попадание в топ', id: 'TOP_VIEWED' },
    // { name: 'Дайджест', id: 'DIGEST' },
    // { name: 'Теги изменены', id: 'TAG_CHANGED' },
]);


export default {
    name: 'NewsList',
    props: {
        mobile: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        CardNewsSkeleton,
        CardNews,
        Search,
        IconSettings,
    },
    data() {
        return {
            model: {
                query: '',
                eventTypes: [],
                onlyUnread: false,
            },
            sortAscent: 0,

            reqtId: 0,
            nextPage: 0,

            elements: [],
            read: [],
            original: null,

            scrolled: false,
            scrollRoot: null,

            subscriptionConfig: null,

            dicts: {
                sortAscent: [ { name: 'Новые', id: 0 }, { name: 'Старые', id: 1 } ],
            },

            filterSheet: 'HIDDEN',
        };
    },
    computed: {
        emptyFilters() {
            return this.sortAscent === 0 && this.model.eventTypes.length === 0 && this.model.onlyUnread === false;
        },
        emptyFiltersAndQuery() {
            return this.emptyFilters && this.model.query === '';
        },
        notFound() {
            return this.original?.result.length === 0 && this.elements.length === 0 && !this.hasNext;
        },
        mapEventTypes() {
            return toMap(this.eventTypes, 'id', it => it.name);
        },
        eventTypes() {
            return EVENT_TYPES_BASE;
        },
        hasNext() {
            return this.reqtId === 0 || this.original?.result.length !== 0 || this.nextPage === 0;
        },
    },
    inject: {
        unreadNewsCount: {
            type: Number,
            default: 0,
        },
        updateUnreadCounter: {
            type: Function,
            default: () => {
                console.error('updateUnreadCounter is not provided');
            },
        },
    },
    created() {
        this.$options.unwatchQuery = this.$watch((vm) => [
            vm.model.query,
            vm.model.eventTypes,
            vm.model.onlyUnread,
            vm.sortAscent,
        ], this.resetSearch);

        this.$options.unwatchRead = this.$watch((vm) => vm.read, debounceAsync(this.sendRead, 10000));
    },
    mounted() {
        document.addEventListener('scroll', this.onScroll, true);
    },
    destroyed() {
        this.$options.unwatchQuery();
        this.$options.unwatchRead();
        document.removeEventListener('scroll', this.onScroll, true);
    },
    methods: {
        async loadNextPage() {
            if (!this.hasNext)
                return false;

            const reqtId = ++this.reqtId;
            const reqt = {
                currentPage: this.nextPage,
                showPerPage: 10,
                sortAscent: this.sortAscent,
                params: this.model,
            };

            try {
                const resp = this.original = await search(reqt);

                if (reqtId !== this.reqtId)
                    return;

                this.elements = this.elements.concat(resp.result.filter((it) => this.mapEventTypes[it.eventType]));

                this.nextPage++;

                this.updateUnreadCounter();

                return true;
            } catch (e) {
                console.warn(e);
            }
        },
        resetSearch() {
            this.elements.splice(0);

            this.nextPage = 0;
            this.$refs.scroll.restart();
        },
        onInputQuery(query) {
            this.model.query = query;
        },
        onScroll() {
            this.scrolled = !!this.$refs.middle.scrollTop;
        },
        onClear() {
            this.model.eventTypes = [];
            this.model.onlyUnread = false;
            this.sortAscent = 0;
            this.resetSearch();
        },
        async close() {
            this.$emit('closeModal');
        },
        updateSubscriptionConfig() {
            this.subscriptionConfig = Local.get(SUBSCRIPTION_CONFIG_KEY, {});
        },
        onRead(id) {
            this.read.push(id);
        },
        async sendRead() {
            if (!this.read.length)
                return;

            const data = {
                ids: this.read,
            };

            await read(data);
            this.read.splice(0);

            this.updateUnreadCounter();
        },
        async readAll() {
            await readAll();

            for (const item of this.$refs.cardNews) {
                item.read = true;
            }

            this.updateUnreadCounter();

            if (this.model.onlyUnread)
                this.resetSearch();
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~@dsee/mixins.scss';

.news-list {
    padding-top: 32px;
    box-sizing: border-box;
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;

    .header {
        flex: none;
        padding: 0 32px;
        position: relative;

        &.shadow {
            box-shadow: 0px 4px 8px rgba(28, 41, 61, 0.1);
        }
    }

    .filter {
        padding-bottom: 20px;

        .row {
            display: flex;
            margin-top: 12px;
            justify-content: space-between;
        }

        .item-list {
            display: flex;
            flex-wrap: nowrap;
            gap: 12px;

            :deep(.item) {
                .label-value {
                    .value {
                        max-width: 96px;
                    }
                }
            }
        }

        .action {
            margin-left: 10px;
        }
    }

    .scroll {
        flex: auto;
        padding: 0 32px;
        @include scrollbar;
    }

    .no-elements {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        padding: 24px 0 32px;
        margin: 0 32px;
        border-radius: 4px;
        border: 1px solid rgba(16, 24, 40, 0.10);

        .icon {
            @include icon(100px, 100px);
            cursor: default;
            background-image: url('./icons/notFound.png');
            margin-bottom: 16px;
        }

        .text {
            font-weight: 500;
        }
    }

    .not-found {
        text-align: center;
        margin-top: 10%;

        .title {
            color: #000000;
            font-size: 18px;
            line-height: 24px;
            margin-bottom: 12px;
            font-weight: bold;
        }

        .desc {
            font-size: 14px;
            line-height: 20px;
        }
    }

    .footer {
        flex: none;
        display: flex;
        height: 40px;
        background: linear-gradient(0deg, rgba(16, 24, 40, 0.03), rgba(16, 24, 40, 0.03)), #FFFFFF;
        box-shadow: 0px 2px 6px rgba(28, 41, 61, 0.1);

        .action {
            width: 100%;
            justify-content: center;
        }
    }

    .filter-sheet .item {
        padding: 20px 0;
    }
}
</style>
